import type { Tone } from "../../ui/StatusTag";

/**
 * ユーザー管理・ドメイン設定の選択肢(`server/app/idp/constants.py` と同じ値)。
 *
 * **真実はサーバー側**。ここは画面の見せ方(並び・ラベル)だけを持つ。
 */

/** スイートのロール。実体は Keycloak の realm ロール(docs/roles.md)。 */
export const ROLE_APPS = ["hr", "mdm", "idp", "dlp"] as const;
export const ROLE_LEVELS = ["viewer", "operator", "approver", "admin"] as const;
export const SUITE_ADMIN_ROLE = "suite:admin";
export const ROLE_APP_LABEL: Record<string, string> = {
  hr: "人事",
  mdm: "デバイス管理",
  idp: "ID管理",
  dlp: "DLP",
};
export const ROLE_LEVEL_LABEL: Record<string, string> = {
  viewer: "閲覧のみ",
  operator: "運用担当",
  approver: "承認者",
  admin: "管理者",
};

/** 操作者。`Me` の必要なところだけ(選択肢は誰が見ているかで変わる)。 */
export type RoleActor = { roles: string[]; is_suite_admin: boolean } | undefined;

function adminApps(me: RoleActor): Set<string> {
  if (me?.is_suite_admin) return new Set<string>(ROLE_APPS);
  return new Set((me?.roles ?? []).filter((r) => r.endsWith(":admin")).map((r) => r.split(":")[0]));
}

function appGroups(me: RoleActor, withAdmin: boolean) {
  const apps = adminApps(me);
  return ROLE_APPS.map((app) => ({
    label: ROLE_APP_LABEL[app],
    options: ROLE_LEVELS.filter((level) => level !== "admin" || (withAdmin && apps.has(app))).map(
      (level) => ({
        value: `${app}:${level}`,
        label: `${app}:${level}(${ROLE_LEVEL_LABEL[level]})`,
      }),
    ),
  }));
}

/**
 * ユーザーに付けられるロールの選択肢(`server/app/idp/roles.py` と同じ規則)。
 *
 * `suite:admin` は suite:admin だけ、`<app>:admin` はその app の admin だけに出す。
 * **真実はサーバー側**(出しても API が 403 で断る)。
 */
export function roleOptions(me: RoleActor) {
  const groups = appGroups(me, true);
  if (!me?.is_suite_admin) return groups;
  return [
    { label: "スイート全体", options: [{ value: SUITE_ADMIN_ROLE, label: "suite:admin(全部の管理)" }] },
    ...groups,
  ];
}

/** API クライアントのスコープ。`suite:admin` は**誰にも出さない**。 */
export function scopeOptions(me: RoleActor) {
  return appGroups(me, Boolean(me?.is_suite_admin));
}

export const USER_ENABLED = "有効";
export const USER_DISABLED = "無効";
export const USER_LOCKED = "一時ロック";

/** 状態 → StatusTag の tone(一覧・パネル・詳細で同じ対応)。 */
export function userStatusTone(status: string): Tone {
  if (status === USER_ENABLED) return "success";
  if (status === USER_LOCKED) return "warning";
  return "neutral";
}

/** 一覧・CSV の列(`EXPORT_HEADER` と同じ並び)。 */
export const USER_COLUMN_LABEL = {
  username: "ユーザー名",
  displayName: "表示名",
  email: "ログインID",
  status: "状態",
  policy: "ポリシー",
  lastLogin: "最新ログイン",
} as const;

/** 一括登録。 */
export const BULK_MODES = [
  { value: "create", label: "一括登録(新しく作る)" },
  { value: "update", label: "一括更新(すでに居る人を直す)" },
];
export const IMPORT_MAX_BYTES = 2 * 1024 * 1024;
export const IMPORT_MAX_ROWS = 1000;
export const ROW_OK = "ok";
export const ROW_ERROR = "error";
/** 取込ジョブの状態(サーバーの `BULK_STATUSES` と同じ)。 */
export const BULK_RUNNING = "running";
export const BULK_DONE = "done";
/** 実行中のジョブを聞き直す間隔。 */
export const BULK_POLL_MS = 2000;

/** カスタム属性。 */
export const CUSTOM_ATTR_MAX = 15;
export const ATTR_NAME_HINT = "英字で始まる 63 文字までの英数字・`_` `.` `-`";

/** ドメイン設定の範囲(サーバーと同じ。画面でも先に落とす)。 */
export const PW_LENGTH_MIN = 8;
export const PW_LENGTH_MAX = 64;
export const PW_COMPLEXITY_MAX = 10;
export const PW_HISTORY_MAX = 10;
export const PW_EXPIRY_MAX_DAYS = 3650;
export const SSO_IDLE_MIN_MINUTES = 1;
export const SSO_IDLE_MAX_MINUTES = 24 * 60;
export const SSO_MAX_MIN_HOURS = 1;
export const SSO_MAX_MAX_HOURS = 24 * 30;
export const LOCKOUT_COUNT_MAX = 100;
export const LOCKOUT_MINUTES_MAX = 24 * 60;
export const OTP_DIGITS = [6, 8];
export const OTP_PERIODS = [30, 60];
export const LOCALES = [
  { value: "ja", label: "日本語" },
  { value: "en", label: "English" },
];
export const TIMEZONES = ["Asia/Tokyo", "UTC"];
/** ロゴ(スイートの設定に data URL で持ち、ログイン画面が絶対 URL で読む)。 */
/** 画像そのものの上限。サーバーの `LOGO_DECODED_MAX_BYTES` と同じ値。 */
export const LOGO_MAX_FILE_BYTES = 512 * 1024;
/** data URL は base64 で 4/3 に膨らむ。サーバーの `DOMAIN_LOGO_MAX_BYTES` と同じ値。
 * SP のロゴ(`spOptions.LOGO_MAX_BYTES` = 64KB)とは別物なので名前を分ける。 */
export const DOMAIN_LOGO_MAX_BYTES = 700_000;
/** 寸法の上限(サーバーもヘッダを読んで同じ値で断る)。アニメーションは不可。 */
export const LOGO_MAX_SIDE = 1024;
/** SVG は受け付けない(スクリプトを書けるため。サーバーも中身で判定して断る)。 */
export const LOGO_ACCEPT = ".png,.jpg,.jpeg,.gif,.webp";
export const LOGO_FORMATS = "PNG/JPEG/GIF/WebP";
